import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageHeaderSection from '../../components/layout/PageHeaderSection';
import PageLoadingShell from '../../components/layout/PageLoadingShell';
import { getAdminReportSettings, saveAdminReportSettings } from '../../api/reportSettings';
import { useReportSettings } from '../../context/ReportSettingsContext';
import { getAdminReportToggles } from '../../config/reportDefinitions';
import { TextInput, CheckboxField } from '../../components/common/form';
import { useUnsavedChanges } from '../../context/UnsavedChangesContext';

const REPORT_GROUPS = [
  {
    id: 'finance',
    keys: [
      'coach-revenue',
      'academy-revenue',
      'payments-due',
      'monthly-statements',
      'session-statement',
    ],
  },
  {
    id: 'attendance',
    keys: ['batch-attendance', 'student-attendance', 'sessions-summary'],
  },
  {
    id: 'export',
    keys: ['data-export'],
  },
];

function parseCommissionPercent(value) {
  if (value === '' || value === null || value === undefined) return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function commissionPercentError(value, t) {
  const num = parseCommissionPercent(value);
  if (num === null) return t('settings.report.percentError');
  if (num < 0 || num > 100) return t('settings.report.percentRange');
  return '';
}

function roleLabel(role, t) {
  return t(`faq.roles.${role}`, { defaultValue: role });
}

export default function ReportSettings() {
  const { t } = useTranslation();
  const { refresh } = useReportSettings();
  const adminReportToggles = getAdminReportToggles(t);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    getAdminReportSettings()
      .then((data) => setForm({
        ...data,
        batchAcademyCommissionPercent: Number(data.batchAcademyCommissionPercent ?? 30),
        individualAcademyCommissionPercent: Number(data.individualAcademyCommissionPercent ?? 20),
      }))
      .catch(() => setError(t('settings.report.loadFailed')));
  }, [t]);

  const togglesByKey = useMemo(() => {
    const map = {};
    adminReportToggles.forEach((report) => {
      map[report.key] = report;
    });
    return map;
  }, [adminReportToggles]);

  const enabledCount = useMemo(() => {
    if (!form) return 0;
    return adminReportToggles.filter((r) => form[r.enabledField] !== false).length;
  }, [form, adminReportToggles]);

  function setField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function toggleReport(enabledField) {
    setForm((prev) => ({ ...prev, [enabledField]: !prev[enabledField] }));
  }

  const persist = useCallback(async () => {
    if (!form) return;
    if (form.coachRevenueEnabled !== false) {
      const batchErr = commissionPercentError(form.batchAcademyCommissionPercent, t);
      const individualErr = commissionPercentError(form.individualAcademyCommissionPercent, t);
      if (batchErr || individualErr) {
        setError(batchErr || individualErr);
        throw new Error(batchErr || individualErr);
      }
    }
    const payload = { ...form };
    if (form.coachRevenueEnabled !== false) {
      payload.batchAcademyCommissionPercent = parseCommissionPercent(form.batchAcademyCommissionPercent);
      payload.individualAcademyCommissionPercent = parseCommissionPercent(form.individualAcademyCommissionPercent);
    }
    await saveAdminReportSettings(payload);
    await refresh();
  }, [form, refresh, t]);

  const { markSaved } = useUnsavedChanges(form, {
    enabled: !!form,
    onSave: persist,
  });

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setSuccess('');
    setError('');

    try {
      await persist();
      markSaved(form);
      setSuccess(t('settings.report.saveSuccess'));
    } catch (err) {
      setError(apiErrorMessage(err, t('settings.report.saveFailed')));
    } finally {
      setSaving(false);
    }
  }

  if (!form) {
    if (error) {
      return (
        <div className="page-shell-form">
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        </div>
      );
    }
    return (
      <PageLoadingShell
        variant="page-shell-form"
        title={t('settings.report.title')}
        subtitle={t('settings.report.subtitle')}
      />
    );
  }

  const batchErr = commissionPercentError(form.batchAcademyCommissionPercent, t);
  const individualErr = commissionPercentError(form.individualAcademyCommissionPercent, t);
  const batchPct = parseCommissionPercent(form.batchAcademyCommissionPercent);
  const individualPct = parseCommissionPercent(form.individualAcademyCommissionPercent);
  const commissionInvalid = form.coachRevenueEnabled !== false && (batchErr || individualErr);

  return (
    <div className="page-shell-form">
      <PageHeaderSection
        eyebrow={t('nav.reports')}
        title={t('settings.report.title')}
        subtitle={t('settings.report.subtitle')}
      />

      <form onSubmit={handleSave} className="space-y-6">
        {success && (
          <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
            {success}
          </div>
        )}
        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Section 1 — which reports */}
        <section className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4 sm:px-6">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">
                  {t('settings.report.stepOne')}
                </p>
                <h2 className="mt-1 text-lg font-semibold text-slate-900">
                  {t('settings.report.availableReports')}
                </h2>
                <p className="mt-1 max-w-2xl text-sm text-slate-600">
                  {t('settings.report.availableReportsDesc')}
                </p>
              </div>
              <div className="shrink-0 rounded-xl border border-slate-200 bg-white px-3 py-2 text-center">
                <p className="text-xs font-medium text-slate-500">{t('settings.report.enabledCountLabel')}</p>
                <p className="text-lg font-semibold tabular-nums text-slate-900">
                  {enabledCount}/{adminReportToggles.length}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-8 px-5 py-5 sm:px-6">
            {REPORT_GROUPS.map((group) => {
              const reports = group.keys.map((key) => togglesByKey[key]).filter(Boolean);
              if (!reports.length) return null;
              return (
                <div key={group.id}>
                  <h3 className="mb-3 text-sm font-semibold text-slate-800">
                    {t(`settings.report.groups.${group.id}`)}
                  </h3>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {reports.map((report) => {
                      const on = form[report.enabledField] !== false;
                      return (
                        <label
                          key={report.key}
                          className={`flex cursor-pointer gap-3 rounded-xl border p-4 transition-colors ${
                            on
                              ? 'border-primary-200 bg-primary-50/40 hover:border-primary-300'
                              : 'border-slate-200 bg-slate-50/60 hover:border-slate-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="mt-1 h-4 w-4 shrink-0 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                            checked={on}
                            onChange={() => toggleReport(report.enabledField)}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-sm font-semibold text-slate-900">{report.label}</span>
                              <span
                                className={`rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${
                                  on
                                    ? 'bg-emerald-100 text-emerald-800'
                                    : 'bg-slate-200 text-slate-600'
                                }`}
                              >
                                {on ? t('settings.report.statusOn') : t('settings.report.statusOff')}
                              </span>
                            </div>
                            <p className="mt-1 text-sm leading-snug text-slate-600">{report.description}</p>
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
                                {t('settings.report.visibleTo')}
                              </span>
                              {report.roles.map((role) => (
                                <span
                                  key={role}
                                  className="rounded-md bg-white px-1.5 py-0.5 text-[11px] font-medium text-slate-700 ring-1 ring-slate-200"
                                >
                                  {roleLabel(role, t)}
                                </span>
                              ))}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Section 2 — commission */}
        {form.coachRevenueEnabled !== false && (
          <section className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4 sm:px-6">
              <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">
                {t('settings.report.stepTwo')}
              </p>
              <h2 className="mt-1 text-lg font-semibold text-slate-900">
                {t('settings.report.coachRevenue')}
              </h2>
              <p className="mt-2 text-xs font-medium uppercase tracking-wide text-slate-500">
                {t('settings.report.appliesTo')}
              </p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                <span className="rounded-md bg-white px-2 py-1 text-sm font-medium text-slate-800 ring-1 ring-slate-200">
                  {t('reports.tabs.coach-revenue')}
                </span>
                <span className="rounded-md bg-white px-2 py-1 text-sm font-medium text-slate-800 ring-1 ring-slate-200">
                  {t('reports.tabs.academy-revenue')}
                </span>
              </div>
              <p className="mt-3 max-w-2xl text-sm text-slate-600">{t('settings.report.coachRevenueDesc')}</p>
            </div>
            <div className="grid grid-cols-1 gap-4 px-5 py-5 sm:grid-cols-2 sm:px-6">
              <TextInput
                label={t('settings.report.batchAcademyShare')}
                type="number"
                min={0}
                max={100}
                step={0.5}
                error={batchErr || undefined}
                note={!batchErr ? t('settings.report.splitNote', { coach: (100 - batchPct).toFixed(0), academy: batchPct.toFixed(0) }) : undefined}
                value={form.batchAcademyCommissionPercent}
                onChange={(e) => setField('batchAcademyCommissionPercent', e.target.value)}
              />
              <TextInput
                label={t('settings.report.individualAcademyShare')}
                type="number"
                min={0}
                max={100}
                step={0.5}
                error={individualErr || undefined}
                note={!individualErr ? t('settings.report.splitNote', { coach: (100 - individualPct).toFixed(0), academy: individualPct.toFixed(0) }) : undefined}
                value={form.individualAcademyCommissionPercent}
                onChange={(e) => setField('individualAcademyCommissionPercent', e.target.value)}
              />
            </div>
          </section>
        )}

        {/* Section 3 — batch attendance options */}
        {form.batchAttendanceEnabled !== false && (
          <section className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4 sm:px-6">
              <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">
                {t('settings.report.stepThree')}
              </p>
              <h2 className="mt-1 text-lg font-semibold text-slate-900">
                {t('settings.report.batchAttendance')}
              </h2>
              <p className="mt-2 text-xs font-medium uppercase tracking-wide text-slate-500">
                {t('settings.report.appliesTo')}
              </p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                <span className="rounded-md bg-white px-2 py-1 text-sm font-medium text-slate-800 ring-1 ring-slate-200">
                  {t('reports.tabs.batch-attendance')}
                </span>
              </div>
              <p className="mt-3 max-w-2xl text-sm text-slate-600">{t('settings.report.batchAttendanceDesc')}</p>
            </div>
            <div className="px-5 py-5 sm:px-6">
              <CheckboxField
                layout="toggle-card"
                label={t('settings.report.showCharges')}
                description={t('settings.report.showChargesDesc')}
                checked={form.coachBatchAttendanceShowCharges === true}
                onChange={() => setField('coachBatchAttendanceShowCharges', !form.coachBatchAttendanceShowCharges)}
              />
            </div>
          </section>
        )}

        <div className="sticky bottom-4 z-10 flex justify-end">
          <button
            type="submit"
            disabled={saving || commissionInvalid}
            className="rounded-xl bg-primary-600 px-6 py-3 text-sm font-semibold text-white shadow-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? t('forms.common.saving') : t('settings.report.saveSettings')}
          </button>
        </div>
      </form>
    </div>
  );
}
